import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel, Typography, ThemeProvider, CssBaseline, Grid, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { User, DEPARTMENTS, ROLES } from "../user";
import { insertNewUser } from "../routes/newUser";
import darkThemeCustom from "../../../views/visuals/darkThemeCustom";
import lightThemeCustom from "../../../views/visuals/lightThemeCustom";
import { updateUser } from "../routes/updateUser";
import { LogItem } from "../../../../api/logItem";
import ProjectActionButton from "../../../common/ui/ProjectActionButton";

export interface UserFormProps {
  twoColumns: boolean;
  user?: User;
  refreshUserList?: () => void; // Aggiungi questa prop
}

const UserForm: React.FC<UserFormProps> = ({ twoColumns, user, refreshUserList }) => {
  // Costante per il valore predefinito
  const DEFAULT_COMPANY = "SLOW TOUR TUSCANY";
  const [formData, setFormData] = useState<User | null>(null);
  const defaultUser: User = {
    firstName: "",
    lastName: "",
    email: "",
    role: 3, // Default: User
    company: DEFAULT_COMPANY,
    lastLogin: null,
    password: undefined,
    isActive: false,
  };

  useEffect(() => {
    if (user) {
      setFormData(user); // Popola il form con i dati dell'utente esistente
    } else {
      setFormData(defaultUser);
    }
  }, [user]);

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertColor, setAlertColor] = useState<"green" | "red" | null>(null);
  const [isTwoColumns, setIsTwoColumns] = useState<boolean>(twoColumns);
  const [showPassword, setShowPassword] = useState(false);

  const roleValue = formData && ROLES[formData.role] ? String(formData.role) : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (name) {
      formData &&
        setFormData({
          ...formData,
          [name]: name === "departmentId" ? Number(value) : value,
        });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formData &&
      setFormData({
        ...formData,
        isActive: e.target.checked,
      });
  };

  // Regex per validare una password complessa
  const passwordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) {
      setAlertMessage("Si è verificato un errore imprevisto.");
      setAlertColor("red");
      return;
    }

    if (!formData.id) {
      // individuo se l'inserimento è per un nuovo utente
      // Validazione della password
      if (!passwordValidationRegex.test(formData.password!)) {
        setAlertMessage("La password deve contenere almeno 8 caratteri, una lettera maiuscola, un numero e un carattere speciale.");
        setAlertColor("red");
        return;
      }
    } else if (formData.password) {
      if (!passwordValidationRegex.test(formData.password)) {
        setAlertMessage("La password deve contenere almeno 8 caratteri, una lettera maiuscola, un numero e un carattere speciale.");
        setAlertColor("red");
        return;
      }
    }

    try {
      let res: Response = new Response();
      const logItem: LogItem = {
        id: null,
        projectCode: "",
        projectPhase: 0,
        userId: formData.id!,
        contentType: "Utente",
        action: "create",
        value: JSON.stringify(formData),
        accessDate: new Date(),
      };
      if (formData.id == null) {
        // Se non esiste un ID, significa che stiamo creando un nuovo utente
        res = await insertNewUser(formData, logItem);
      } else {
        res = await updateUser(formData, logItem);
      }

      const data = await res.json();

      if (res.ok) {
        setAlertMessage(data.message);
        setAlertColor("green");
        refreshUserList && refreshUserList();
      } else {
        setAlertMessage(data.message);
        setAlertColor("red");
      }
    } catch (error) {
      setAlertMessage("Si è verificato un errore imprevisto.");
      setAlertColor("red");
    }

    setTimeout(() => {
      setAlertMessage(null);
      setAlertColor(null);
    }, 5000);
  };

  const savedMode = localStorage.getItem("themeMode");

  return (
    <ThemeProvider theme={savedMode === "dark" ? darkThemeCustom : lightThemeCustom}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start", // Align to top for scrollable content
          height: "auto",
          maxHeight: "90vh", // Max height for the form container
          overflowY: "auto", // Allow scrolling within the form box
          p: { xs: 1, sm: 2 }, // Responsive padding
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5, // Reduced gap
            width: "100%",
            maxWidth: 500, // Slightly reduced max width
            p: { xs: 1.5, sm: 2.5 }, // Responsive padding
            borderRadius: (theme) => theme.shape.borderRadius,
            boxShadow: "none",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center", fontWeight: 500, mb: 1 }}>
            {formData?.id ? "Modifica Utente" : "Nuovo Utente"}
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={isTwoColumns ? 6 : 12}>
              <TextField label="Nome" name="firstName" value={formData ? formData.firstName : ""} onChange={handleChange} fullWidth required size="small" />
            </Grid>

            <Grid item xs={12} sm={isTwoColumns ? 6 : 12}>
              <TextField label="Cognome" name="lastName" value={formData ? formData.lastName : ""} onChange={handleChange} fullWidth required size="small" />
            </Grid>

            <Grid item xs={12} sm={isTwoColumns ? 6 : 12}>
              <TextField label="Email" name="email" value={formData ? formData.email : ""} onChange={handleChange} fullWidth required type="email" size="small" />
            </Grid>

            <Grid item xs={12} sm={isTwoColumns ? 6 : 12}>
              <FormControl fullWidth size="small">
                <InputLabel id="role-label">Ruolo</InputLabel>
                <Select
                  labelId="role-label"
                  name="role"
                  label="Ruolo"
                  value={roleValue}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    formData &&
                      setFormData({
                        ...formData,
                        [name!]: Number(value),
                      });
                  }}
                >
                  {Object.entries(ROLES).map(([value, label]) => (
                    <MenuItem key={value} value={String(value)}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={isTwoColumns ? 6 : 12}>
              <TextField
                label="Password"
                name="password"
                value={formData?.password || ""} // Ensure value is not undefined
                onChange={handleChange}
                fullWidth
                required={!formData?.id} // Required only for new users
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => setShowPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        aria-label={showPassword ? "Nascondi password" : "Visualizza password"}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={!formData?.id || formData?.password ? "Min. 8 caratteri, 1 maiusc, 1 num, 1 speciale." : "Lasciare vuoto per non modificare"}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={isTwoColumns ? 6 : 12}>
              <TextField 
              label="Azienda" 
              name="company" 
              value={formData ? formData.company : ""} 
              onChange={handleChange} 
              fullWidth 
              required 
              size="small" 
              disabled={!formData?.id} // Disabilitato se non c'è ID (nuovo utente)
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData ? formData.isActive : false}
                    onChange={handleSwitchChange}
                    name="isActive"
                    size="small"
                    sx={{
                      "& .MuiSwitch-thumb": {
                        // backgroundColor handled by theme or default
                      },
                      "& .MuiSwitch-track": {
                        // backgroundColor handled by theme or default
                      },
                    }}
                  />
                }
                label={formData?.isActive ? "Attivo" : "Non attivo"}
                sx={{ ".MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
              />
            </Grid>
          </Grid>

          {alertMessage && (
            <Typography
              sx={{
                color: alertColor === "green" ? "success.main" : "error.main",
                textAlign: "center",
                fontWeight: "medium",
                fontSize: "0.8rem",
                mt: 0.5,
              }}
            >
              {alertMessage}
            </Typography>
          )}

          <ProjectActionButton type="submit" variant="contained" color="primary" size="medium" sx={{ mt: 1 }}>
            Salva Utente
          </ProjectActionButton>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default UserForm;
